import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  LogOut, Users, BookOpen, Upload, X, Check, FileText, Calendar, CheckCircle2, XCircle, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { schoolInfo } from '../utils/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TeacherDashboardPage = () => {
  const navigate = useNavigate();
  const [teacherData, setTeacherData] = useState(null);
  const [students, setStudents] = useState([]);
  const [homework, setHomework] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHomeworkModal, setShowHomeworkModal] = useState(false);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  
  const [gradeForm, setGradeForm] = useState({
    status: 'Reviewed',
    grade: '',
    teacher_feedback: ''
  });
  
  const [homeworkForm, setHomeworkForm] = useState({
    title: '',
    description: '',
    image: '',
    date: new Date().toISOString().split('T')[0]
  });

  // Attendance states
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceRecords, setAttendanceRecords] = useState({});

  useEffect(() => {
    verifyTeacherAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyTeacherAndFetch = async () => {
    const isTeacher = localStorage.getItem('isTeacherAuthenticated');
    const storedTeacher = localStorage.getItem('teacherData');
    
    console.log('🔍 Teacher auth check:', { isTeacher, hasStoredData: !!storedTeacher });
    
    if (!isTeacher || !storedTeacher) {
      toast.error('Please login as teacher');
      navigate('/teacher-login');
      return;
    }

    const teacher = JSON.parse(storedTeacher);
    console.log('👨‍🏫 Teacher data from localStorage:', teacher);
    console.log('   - assigned_class:', teacher.assigned_class);
    
    setTeacherData(teacher);
    
    // Ensure class name has "Class " prefix for API calls
    let className = teacher.assigned_class;
    if (className && !className.startsWith('Class ')) {
      className = `Class ${className}`;
      console.log('   ⚠️ Normalized class name:', className);
    }
    
    console.log('📡 Fetching data for class:', className);
    
    await Promise.all([
      fetchStudents(className),
      fetchHomework(className)
    ]);
    
    setLoading(false);
  };

  const fetchStudents = async (className) => {
    console.log('📚 Fetching students for class:', className);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/teachers/class/${className}/students`);
      console.log('✅ Students fetched:', response.data.length, 'students');
      setStudents(response.data);
    } catch (error) {
      console.error('❌ Error fetching students:', error);
      console.error('   URL was:', `${BACKEND_URL}/api/teachers/class/${className}/students`);
      toast.error('Failed to load students');
      setStudents([]); // Set empty array to prevent undefined errors
    }
  };

  const fetchHomework = async (className) => {
    console.log('📝 Fetching homework for class:', className);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/homework/class/${className}`);
      console.log('✅ Homework fetched:', response.data.length, 'items');
      setHomework(response.data);
    } catch (error) {
      console.error('❌ Error fetching homework:', error);
      console.error('   URL was:', `${BACKEND_URL}/api/homework/class/${className}`);
      toast.error('Failed to load homework');
      setHomework([]); // Set empty array to prevent undefined errors
    }
  };

  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem('isTeacherAuthenticated');
    localStorage.removeItem('teacherData');
    
    // Clear sessionStorage (mobile, OTP, etc.)
    sessionStorage.clear();
    
    toast.success('Logged out successfully');
    navigate('/teacher-login');
  };

  const handleHomeworkChange = (e) => {
    setHomeworkForm({
      ...homeworkForm,
      [e.target.name]: e.target.value
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setHomeworkForm({
          ...homeworkForm,
          image: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePublishHomework = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/homework`, {
        ...homeworkForm,
        class_name: teacherData.assigned_class,
        teacher_name: teacherData.teacher_name
      });
      
      toast.success('Homework published successfully!');
      setShowHomeworkModal(false);
      setHomeworkForm({
        title: '',
        description: '',
        image: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchHomework(teacherData.assigned_class);
    } catch (error) {
      toast.error('Failed to publish homework');
    }
  };

  const handleViewSubmissions = async (hw) => {
    setSelectedHomework(hw);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/homework/${hw.id}/submissions`);
      setSubmissions(response.data);
      setShowSubmissionsModal(true);
    } catch (error) {
      toast.error('Failed to load submissions');
    }
  };

  const handleGradeSubmission = (submission) => {
    setSelectedSubmission(submission);
    setGradeForm({
      status: submission.status || 'Reviewed',
      grade: submission.grade || '',
      teacher_feedback: submission.teacher_feedback || ''
    });
    setShowGradeModal(true);
  };

  const handleGradeChange = (e) => {
    setGradeForm({
      ...gradeForm,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmitGrade = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${BACKEND_URL}/api/homework/submissions/${selectedSubmission.id}`,
        gradeForm
      );
      toast.success('Submission graded successfully!');
      setShowGradeModal(false);
      // Refresh submissions
      const response = await axios.get(`${BACKEND_URL}/api/homework/${selectedHomework.id}/submissions`);
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to grade submission');
    }
  };

  const sendHomeworkReminder = (hw) => {
    const message = `📚 AMTEE PUBLIC SCHOOL

New Homework Assigned:

📌 ${hw.title}

📝 ${hw.description}

📅 Due Date: ${hw.date}

Please check the app for details.

- ${teacherData.teacher_name}
${teacherData.assigned_class}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp to notify parents');
  };

  const handleOpenAttendance = () => {
    // Initialize attendance records with all students marked as Present by default
    const initialRecords = {};
    students.forEach(student => {
      initialRecords[student.id] = 'Present';
    });
    setAttendanceRecords(initialRecords);
    setShowAttendanceModal(true);
  };

  const toggleAttendance = (studentId, status) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const handleMarkAllPresent = () => {
    const allPresent = {};
    students.forEach(student => {
      allPresent[student.id] = 'Present';
    });
    setAttendanceRecords(allPresent);
    toast.success('All students marked as Present');
  };

  const handleMarkAllAbsent = () => {
    const allAbsent = {};
    students.forEach(student => {
      allAbsent[student.id] = 'Absent';
    });
    setAttendanceRecords(allAbsent);
    toast.success('All students marked as Absent');
  };

  const handleSubmitAttendance = async () => {
    try {
      const records = students.map(student => ({
        student_id: student.id,
        student_name: student.student_name,
        status: attendanceRecords[student.id] || 'Present'
      }));

      await axios.post(`${BACKEND_URL}/api/attendance/mark`, {
        class_name: teacherData.assigned_class,
        date: attendanceDate,
        teacher_name: teacherData.teacher_name,
        records: records
      });

      toast.success(`Attendance marked for ${attendanceDate}`);
      setShowAttendanceModal(false);
    } catch (error) {
      console.error('Error marking attendance:', error);
      toast.error('Failed to mark attendance');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={schoolInfo.logo} 
                alt="School Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{teacherData?.teacher_name}</h1>
                <p className="text-sm text-gray-600">{teacherData?.assigned_class}</p>
              </div>
            </div>
            <Button 
              size="sm"
              variant="ghost" 
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Welcome Card */}
        <Card className="mb-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
          <CardContent className="py-6">
            <h2 className="text-2xl font-bold mb-2">Welcome, {teacherData?.teacher_name}!</h2>
            <p className="text-blue-100">Class: {teacherData?.assigned_class} • Students: {students.length}</p>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="students" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="students" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Students
            </TabsTrigger>
            <TabsTrigger value="homework" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Homework
            </TabsTrigger>
          </TabsList>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Class Students ({students.length})</span>
                  <Button
                    onClick={handleOpenAttendance}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Mark Attendance
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {students.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No students in this class yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {students.map((student, index) => (
                      <div 
                        key={student.id} 
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{student.student_name}</p>
                            <p className="text-sm text-gray-500">Parent: {student.parent_name}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Homework Tab */}
          <TabsContent value="homework">
            <div className="space-y-6">
              {/* Add Homework Button */}
              <Button
                onClick={() => setShowHomeworkModal(true)}
                className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add New Homework
              </Button>

              {/* Homework List */}
              {homework.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-gray-500">
                    <BookOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No homework assigned yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {homework.map((hw) => (
                    <Card key={hw.id} className="shadow-lg hover:shadow-xl transition-shadow border-l-4 border-l-green-500">
                      <CardHeader className="pb-3 bg-gradient-to-r from-green-50 to-white">
                        <CardTitle className="text-lg flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-green-600" />
                            {hw.title}
                          </span>
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {hw.date}
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <p className="text-gray-700 mb-4">{hw.description}</p>
                        {hw.image && (
                          <img 
                            src={hw.image} 
                            alt="Homework" 
                            className="w-full max-h-64 object-cover rounded-lg mb-4"
                          />
                        )}
                        <div className="space-y-2">
                          <Button
                            onClick={() => sendHomeworkReminder(hw)}
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                          >
                            <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            Notify Parents on WhatsApp
                          </Button>
                          <Button
                            onClick={() => handleViewSubmissions(hw)}
                            variant="outline"
                            className="w-full"
                          >
                            <Check className="h-4 w-4 mr-2" />
                            View Submissions
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Homework Modal */}
      <Dialog open={showHomeworkModal} onOpenChange={setShowHomeworkModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Homework</DialogTitle>
            <DialogDescription>
              Assign homework to {teacherData?.assigned_class}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePublishHomework} className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                name="title"
                value={homeworkForm.title}
                onChange={handleHomeworkChange}
                placeholder="e.g., Math Exercise Chapter 5"
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                name="description"
                value={homeworkForm.description}
                onChange={handleHomeworkChange}
                placeholder="Describe the homework assignment..."
                rows={4}
                required
              />
            </div>
            <div>
              <Label htmlFor="date">Due Date *</Label>
              <Input
                id="date"
                name="date"
                type="date"
                value={homeworkForm.date}
                onChange={handleHomeworkChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="image">Upload Image (Optional)</Label>
              <Input
                id="image"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="cursor-pointer"
              />
              {homeworkForm.image && (
                <div className="mt-2 relative">
                  <img 
                    src={homeworkForm.image} 
                    alt="Preview" 
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setHomeworkForm({...homeworkForm, image: ''})}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowHomeworkModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Upload className="h-4 w-4 mr-2" />
                Publish Homework
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Submissions Modal */}
      <Dialog open={showSubmissionsModal} onOpenChange={setShowSubmissionsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submissions: {selectedHomework?.title}</DialogTitle>
            <DialogDescription>
              Total submissions: {submissions.length}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {submissions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No submissions yet</p>
              </div>
            ) : (
              submissions.map((sub) => (
                <Card key={sub.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{sub.student_name}</p>
                        <p className="text-sm text-gray-500">
                          Submitted: {new Date(sub.submitted_at).toLocaleDateString()}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            sub.status === 'Graded' ? 'bg-green-100 text-green-700' :
                            sub.status === 'Reviewed' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {sub.status || 'Pending'}
                          </span>
                          {sub.grade && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                              Grade: {sub.grade}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleGradeSubmission(sub)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {sub.status === 'Graded' ? 'Edit Grade' : 'Grade'}
                      </Button>
                    </div>
                    {sub.submission_file && (
                      <img 
                        src={sub.submission_file} 
                        alt="Submission" 
                        className="w-full max-h-64 object-contain rounded-lg bg-gray-50 mb-3"
                      />
                    )}
                    {sub.comment && (
                      <div className="bg-gray-50 p-3 rounded-lg mb-3">
                        <p className="text-xs text-gray-500 font-semibold mb-1">Student Comment:</p>
                        <p className="text-sm text-gray-700">{sub.comment}</p>
                      </div>
                    )}
                    {sub.teacher_feedback && (
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <p className="text-xs text-blue-600 font-semibold mb-1">Your Feedback:</p>
                        <p className="text-sm text-gray-700">{sub.teacher_feedback}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark Attendance Modal */}
      <Dialog open={showAttendanceModal} onOpenChange={setShowAttendanceModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-6 w-6 text-blue-600" />
              Mark Attendance
            </DialogTitle>
            <DialogDescription>
              Mark attendance for {teacherData?.assigned_class} on {attendanceDate}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Date Picker */}
            <div>
              <Label htmlFor="attendance-date">Attendance Date</Label>
              <Input
                id="attendance-date"
                type="date"
                value={attendanceDate}
                onChange={(e) => setAttendanceDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Bulk Actions */}
            <div className="flex gap-2">
              <Button
                onClick={handleMarkAllPresent}
                variant="outline"
                className="flex-1 border-green-600 text-green-600 hover:bg-green-50"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark All Present
              </Button>
              <Button
                onClick={handleMarkAllAbsent}
                variant="outline"
                className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Mark All Absent
              </Button>
            </div>

            {/* Student List */}
            <div className="border rounded-lg divide-y">
              {students.map((student, index) => {
                const status = attendanceRecords[student.id] || 'Present';
                return (
                  <div key={student.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 font-medium w-8">{index + 1}.</span>
                      <div>
                        <p className="font-semibold text-gray-900">{student.student_name}</p>
                        <p className="text-xs text-gray-500">Parent: {student.parent_name}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => toggleAttendance(student.id, 'Present')}
                        className={`${
                          status === 'Present'
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Present
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => toggleAttendance(student.id, 'Absent')}
                        className={`${
                          status === 'Absent'
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Absent
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Submit Button */}
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowAttendanceModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitAttendance}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Check className="h-4 w-4 mr-2" />
                Submit Attendance
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Grade Submission Modal */}
      <Dialog open={showGradeModal} onOpenChange={setShowGradeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Grade Submission</DialogTitle>
            <DialogDescription>
              Grade {selectedSubmission?.student_name}'s submission
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitGrade} className="space-y-4">
            {/* Status */}
            <div>
              <Label htmlFor="status">Status *</Label>
              <select
                id="status"
                name="status"
                value={gradeForm.status}
                onChange={handleGradeChange}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Pending">Pending</option>
                <option value="Reviewed">Reviewed</option>
                <option value="Graded">Graded</option>
              </select>
            </div>

            {/* Grade/Marks */}
            <div>
              <Label htmlFor="grade">Grade / Marks</Label>
              <Input
                id="grade"
                name="grade"
                value={gradeForm.grade}
                onChange={handleGradeChange}
                placeholder="e.g., A+, 95/100, Excellent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional: Enter grade, marks, or rating
              </p>
            </div>

            {/* Teacher Feedback */}
            <div>
              <Label htmlFor="teacher_feedback">Teacher Feedback</Label>
              <Textarea
                id="teacher_feedback"
                name="teacher_feedback"
                value={gradeForm.teacher_feedback}
                onChange={handleGradeChange}
                placeholder="Write your feedback for the student..."
                rows={4}
              />
            </div>

            {/* Preview Student Submission */}
            {selectedSubmission?.submission_file && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 font-semibold mb-2">Student Submission:</p>
                <img 
                  src={selectedSubmission.submission_file} 
                  alt="Submission" 
                  className="w-full max-h-32 object-contain rounded-lg bg-white border"
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowGradeModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Save Grade
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherDashboardPage;
